import * as React from 'react';
import styled from 'styled-components';
import { pipe, tuple } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import { RGBColor } from 'react-color';
import { useIORef } from '../../core/hooks';
import { useSetter, useEffect } from './slots';

declare module './slots' {
    interface SelectControls {
        'Color': 'Color',
    }
}


interface ControlColor {
    value: Record<string, any> | null,
    className?: string,
    defaultValue?: Record<string, any>,
}

const isValueForColor = (x: any): x is { color: RGBColor } => 
    pipe(
        x,
        x => tuple(typeof(x) === 'object' && x !== null, x),
        ([s, x]) => s && typeof(x.color) === 'object',
    );

const Component: React.FunctionComponent<ControlColor> = ({ value, className, defaultValue = { color: '#2c2c2c' } }) => {

    const derivedValue = React.useMemo(() => value || defaultValue, [value]);
    
    const {
        read: getValue,
        write: writeValue,
    } = useIORef(derivedValue);

    const [changeValueEffect, run] = useEffect('changeValue');

    React.useEffect(() => {
        run(writeValue)(derivedValue);
    }, [derivedValue]);

    const c = React.useMemo(() => pipe(
        getValue(),
        x => isValueForColor(x) ? O.some(x) : O.none,
        O.fold(
            () => defaultValue.color,
            (x) => x.color,
        ),
    ), [changeValueEffect]);


    const change = run((value) => {
        if(isValueForColor(value)){
            writeValue(value)();
        }
        else {
            console.warn(`
                Тип значения ${JSON.stringify(value)} не поддерживается инпутом. 
                Должен быть объект с полем 'text'!
            `);
        }
    });

    const reset = run(() => {
        writeValue(defaultValue)();
    });


    useSetter('target', {
        tag: 'Color',
        isEmpty: () => false,
        getValue,
        change,
        reset,
    });

    return (
        <Container className={className}>
            <ColorBall style={{ backgroundColor: `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})` }} />
        </Container>
    );
}

export default Component;

const Container = styled.div`
    display: flex;
    align-items: center;
    padding-left: 1em;
    padding-right: 1em;
    flex-grow: 1;
`;

const ColorBall = styled.div`
    border-radius: 50%;
    width: 1.05em;
    height: 1.05em;
    border: .5px solid #C9CCD5;
`;
